import isEqual from 'react-fast-compare';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';

export type OptionalPosField<T> = { value?: T | null; hasChanged?: boolean };

export const posField = <T>(value: T | null, hasChanged = false) => {
  return { value, hasChanged };
};

export const posChangedField = <T>(value: T | null) => {
  return posField(value, true);
};

export const posFieldFromValues = <T>(value: T | null, oldValue?: T | null) => {
  const hasChanged = !isEqual(value, oldValue);
  return posField(value, hasChanged);
};

export const safeSetPosChangedField = <
  TFormValues extends FieldValues,
  TPath extends Path<TFormValues>,
>(
  setValue: UseFormSetValue<TFormValues>,
  fieldName: TPath,
  value: PathValue<TFormValues, TPath>['value']
) => {
  const wrappedValue = posChangedField(value);
  setValue(fieldName, wrappedValue as PathValue<TFormValues, TPath>);
};

export const hasChanged = <T>(
  newP: OptionalPosField<T> | null | undefined,
  oldP: OptionalPosField<T> | null | undefined
) => {
  if (oldP == null) {
    return newP?.hasChanged;
  }

  return newP?.hasChanged && !isEqual(newP.value, oldP.value);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const resetPosFields = <T extends Record<string, any>>(obj: T): T => {
  const result = {} as T;

  for (const [key, value] of Object.entries(obj)) {
    if (
      value &&
      typeof value === 'object' &&
      'hasChanged' in value &&
      'value' in value
    ) {
      // This is a PosField - reset hasChanged to false
      result[key as keyof T] = {
        ...value,
        hasChanged: false,
      } as T[keyof T];
    } else {
      // Keep other values as is
      result[key as keyof T] = value;
    }
  }

  return result;
};
